/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { SignInDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

export type HeaderFavoritesButtonProps = {
  className?: {
    root?: string;
    info?: string;
    title?: string;
    price?: string;
    badge?: string;
  };
};

export function HeaderFavoritesButton({ className }: HeaderFavoritesButtonProps) {
  const isInicialized = useCustomerStore(s => s.isInitialized);

  const openDialog = useDialogStore(s => s.open);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const router = useRouter();

  const me = useCustomerStore(s => s.me);

  const isAuthorited = me && me?.customer && isInicialized;

  if (isAuthorited)
    return (
      <Button size="icon" variant={'outline'} asChild>
        <Link
          href={me ? ROUTES.FAVORITES() : (ROUTES.FAVORITE_PRODUCTS?.() ?? '#')}
          onClick={e => {
            if (me?.customer) return;
            if (
              !me?.customer &&
              templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled
            ) {
              e.preventDefault();
              e.stopPropagation();
              router.push(ROUTES.FAVORITE_PRODUCTS());
              return;
            }
            openDialog({
              id: SignInDialog.id,
              content: <SignInDialog />,
            });
          }}
          prefetch={false}
        >
          <div className="relative">
            <FavoritesBadge />
            {HEADER_CONFIG.headerMain.favorites.icon}
          </div>
        </Link>
      </Button>
    );
}

export function FavoritesBadge() {
  const { me, isInitialized } = useCustomerStore();
  const locale = useLocaleStore(s => s.currentLocale);
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const [localFavoritesCount, setLocalFavoritesCount] = useState<number>(0);

  // Query for favorite products from the server when the user is logged in
  const favoriteItems = useQuery({
    queryKey: [CacheKey.FAVORITE_PRODUCTS, locale, me?.authToken ?? null],
    queryFn: () =>
      fetchProducts({
        locale,
        authToken: me ? me!.authToken : '',
        query: { favorite: true, results: 99 },
      }),
    enabled: isInitialized && !!me?.authToken,
  });

  const favoriteItemsCount = me?.authToken
    ? (favoriteItems.data?.data?.length ?? 0) // Use server data if logged in
    : templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled
      ? localFavoritesCount // Use local storage data if not logged in
      : 0;

  // Handle counting favorite items in localStorage when not authenticated
  useEffect(() => {
    if (!templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled)
      return;
    const getLocalFavoritesCount = () => {
      const storedFavorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]')
        .map(Number)
        .filter(Boolean);
      return storedFavorites.length;
    };

    // Update count on component mount
    if (!me?.authToken) {
      setLocalFavoritesCount(getLocalFavoritesCount());
    }

    // Add storage event listener for changes in localStorage
    const handleStorageChange = () => {
      if (!me?.authToken) {
        setLocalFavoritesCount(getLocalFavoritesCount());
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.authToken]);

  const countText =
    favoriteItemsCount > 99 ? (
      <span>
        99<sup>+</sup>
      </span>
    ) : (
      favoriteItemsCount
    );

  if (favoriteItemsCount)
    return (
      <Badge
        className={cn(
          'pointer-events-none absolute right-0 top-0 aspect-square h-5 w-5 -translate-y-1/2 translate-x-1/2 justify-center rounded-[6px] bg-primary font-normal text-white',
          'ea-favorites-button__badge',
        )}
        size="circle"
      >
        {countText}
      </Badge>
    );
}
